import React from "react";
import dateFormat from 'date-fns/format'

import MUIDataTable from "../../components/DataTable"
import { Button, Typography } from "../../components/Wrappers";
import AddToolButton from '../../components/DataTable/AddToolButton'
import { useCloseDialog, useOpenDialog } from '../../context/DialogContext'
import { useHouseholdAppointments } from "../../apis";
import AppointmentForm from '../appointments/AppointmentForm'
import {
    formatAppointmentTimeRange,
    formatDuration,
    formatHandlerName,
    toLocalDate,
} from '../appointments/calendarHelpers'

/**
 * HouseholdAppointments - the appointments section of a household page
 * (Requirements 11.1 - 11.6). Rendered from Household.js the same way
 * HouseholdCallLogs is: given the loaded household, it owns its own fetch.
 *
 * THE ORDER COMES FROM THE SERVER AND IS NOT TOUCHED HERE. GET
 * /appointments/household/:householdId orders by date DESC then startTime DESC
 * (Requirement 11.3, most recent first) and applies no date filter, so past and
 * future appointments arrive together in one already-correct list
 * (Requirement 11.2). Sorting is therefore disabled on the table rather than
 * left to mui-datatables, which would otherwise re-sort the rows as strings on
 * the first header click and quietly break 11.3.
 *
 * CANCELLED APPOINTMENTS ARE PART OF THIS LIST. The endpoint deliberately
 * returns them because Requirement 11.4 displays `status`, and cancelling is a
 * soft delete so the history stays readable. Nothing is filtered out here.
 *
 * The household-history endpoint includes `notes` for this table only; it does
 * not add notes to handler-week calendar responses.
 *
 * DATES ARE FORMATTED THROUGH toLocalDate, NEVER new Date(row.date).
 * `appointments.date` is a mysql DATE that arrives as '2026-02-15T00:00:00.000Z'
 * - midnight UTC - so parsing it as a timestamp renders the previous day in
 * every US timezone. See the header of calendarHelpers.js.
 */
const HouseholdAppointments = ({ household }) => {
    const householdId = household ? household.id : null
    const openDialog = useOpenDialog()
    const closeDialog = useCloseDialog()
    const { appointments, isLoading, isError, mutate } = useHouseholdAppointments(householdId)

    const openCreateAppointment = () => {
        openDialog(
            'New Appointment',
            <AppointmentForm
                householdId={householdId}
                lockHousehold
                onSaved={() => {
                    mutate()
                    closeDialog()
                }}
                onClose={closeDialog}
            />,
            { dialogProps: { maxWidth: 'lg' } }
        )
    }

    // AppointmentForm owns the shared edit/cancel behavior. Its onSaved callback
    // covers both modes, so the history refreshes and closes after either write.
    const openEditAppointment = appointment => {
        const appointmentId = appointment && appointment.id
        if (appointmentId === null || appointmentId === undefined || appointmentId === '') return

        openDialog(
            'Edit Appointment',
            <AppointmentForm
                appointmentId={appointmentId}
                onSaved={() => {
                    mutate()
                    closeDialog()
                }}
                onClose={closeDialog}
            />,
            { dialogProps: { maxWidth: 'lg' } }
        )
    }

    //Requirement 11.4 - spelled out in words, so status is never colour alone
    const statusLabels = { scheduled: "Scheduled", cancelled: "Cancelled" }

    // Requirements 11.5 / 11.6 - the section is always on the page; only the
    // body text changes. An error must not read as "no appointments", so the
    // failure case says so instead of showing the empty state.
    const bodyMessage = isError
        ? "The appointments for this household could not be loaded."
        : isLoading
            ? "Loading appointments\u2026"
            : "No appointments exist for this household."

    const options = {
        //see the header: the server's date/time DESC order is the display order
        sort: false,
        filter: false,
        rowsPerPage: 5,
        textLabels: { body: { noMatch: bodyMessage } },
        customToolbar: () => <AddToolButton handleClick={openCreateAppointment} />,
    }

    const columns = [
        {
            name: "date",
            label: "Date",
            options: {
                customBodyRender: value => {
                    const localDate = toLocalDate(value)
                    return localDate ? dateFormat(localDate, "MMM do, yyyy") : ""
                },
            },
        },
        {
            name: "startTime",
            label: "Time",
            options: {
                customBodyRenderLite: dataIndex => {
                    const row = appointments[dataIndex];
                    if (!row) return ''
                    const duration = formatDuration(row.durationMinutes)
                    return (
                        <div>
                            <Typography color="text">{formatAppointmentTimeRange(row)}</Typography>
                            {!!duration && <Typography color="text" size="sm" colorBrightness="secondary">{duration}</Typography>}
                        </div>
                    )
                },
            },
        },
        {
            name: "appointmentType",
            label: "Appointment Type",
            options: { customBodyRender: value => (value ? value.name : "") },
        },
        {
            //the handler relation is fetched by this endpoint for exactly this
            name: "handler",
            label: "Handler",
            options: { customBodyRender: value => formatHandlerName(value) },
        },
        {
            name: "status",
            label: "Status",
            options: { customBodyRender: value => statusLabels[value] || value || "" },
        },
        {
            name: "notes",
            label: "Notes",
            options: {
                customBodyRender: value => {
                    const notes = typeof value === 'string' ? value : ''
                    return notes ? (
                        <Typography color="text" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {notes}
                        </Typography>
                    ) : ''
                },
            },
        },
        {
            name: "actions",
            label: "Actions",
            options: {
                filter: false,
                sort: false,
                empty: true,
                customBodyRenderLite: dataIndex => {
                    const row = appointments[dataIndex]
                    if (!row || row.id === null || row.id === undefined || row.id === '') return ''
                    return (
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            aria-label="Edit appointment"
                            onClick={() => openEditAppointment(row)}
                        >
                            Edit
                        </Button>
                    )
                },
            },
        },
    ];

    return (
        <>
            {/* Requirement 11.6 - the failure is announced inside the section,
                with a way to try again, rather than leaving an empty table */}
            {!!isError && (
                <div role="alert">
                    <Typography color="secondary">
                        The appointments for this household could not be loaded.
                    </Typography>
                    <Button size="small" variant="outlined" color="primary" onClick={() => mutate()}>
                        Retry
                    </Button>
                </div>
            )}
            <MUIDataTable title={"Appointments"} loading={isLoading && !isError} data={appointments} columns={columns} options={options} />
        </>
    )
}

export default HouseholdAppointments
